import React from 'react';
import styled from 'styled-components';
import StarRating from '../../product/StarRating';

const CardContainer = styled.div`
  border-radius: 1rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: scale(1.03);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const CardTitle = styled.h3`
  font-weight: 600;
  font-size: 1.125rem;
  margin: 0;
`;

const RoleBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.1);
  text-transform: capitalize;
`;

const CategoryBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  background: rgba(92, 233, 255, 0.15);
  color: #5ce9ff;
  border: 1px solid rgba(92, 233, 255, 0.3);
  text-transform: capitalize;
`;

const StatusBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  text-transform: capitalize;
  background: ${({ status }) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'published':
      case 'active':
        return 'rgba(0, 247, 210, 0.2)';
      case 'pending':
      case 'review':
        return 'rgba(255, 193, 7, 0.2)';
      case 'rejected':
      case 'inactive':
      case 'suspended':
        return 'rgba(248, 113, 113, 0.2)';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${({ status }) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'published':
      case 'active':
        return '#00f7d2';
      case 'pending':
      case 'review':
        return '#ffc107';
      case 'rejected':
      case 'inactive':
      case 'suspended':
        return '#f87171';
      default:
        return '#9ca3af';
    }
  }};
  border: 1px solid ${({ status }) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'published':
      case 'active':
        return 'rgba(0, 247, 210, 0.3)';
      case 'pending':
      case 'review':
        return 'rgba(255, 193, 7, 0.3)';
      case 'rejected':
      case 'inactive':
      case 'suspended':
        return 'rgba(248, 113, 113, 0.3)';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  }};
`;

const CardDescription = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin-top: 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RatingContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RatingText = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const CardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.25rem;
`;

const ViewButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  font-size: 0.75rem;
`;

const EditButton = styled.button`
  color: #5ce9ff;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const DeleteButton = styled.button`
  color: #f87171;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const StoreCard = ({ store, onView, onEdit, onDelete }) => {
  console.log("🚀 ~ StoreCard ~ store:", store)
  return (
    <CardContainer
      whileHover={{ scale: 1.03 }}
      onClick={onView}
    >
      {store.coverImage && (
        <CardImage
          src={store.coverImage}
          alt={store.name}
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1584017911766-d451b3d0e843';
          }}
        />
      )}
      <CardContent>
        <CardHeader>
          <CardTitle>{store.name}</CardTitle>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {(store.status || store.originalData?.status) && (
              <StatusBadge status={store.originalData?.status || store.status}>
                {store.originalData?.status || store.status}
              </StatusBadge>
            )}
            {store.role && (
              <RoleBadge>{store.role}</RoleBadge>
            )}
            {store?.originalData?.category?.category_name && (
              <CategoryBadge>{store?.originalData?.category.category_name}</CategoryBadge>
            )}
          </div>
        </CardHeader>
        <CardDescription>{store.shortDescription}</CardDescription>
        
        <RatingContainer>
          <StarRating rating={store.rating || 0} />
          <RatingText>({store.rating || 0})</RatingText>
        </RatingContainer>

        <CardActions>
          <ViewButton
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
          >
            View Store
          </ViewButton>
          {(onEdit || onDelete) && (
            <ActionButtons>
              {onEdit && (
                <EditButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                >
                  Edit
                </EditButton>
              )}
              {onDelete && (
                <DeleteButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  Delete
                </DeleteButton>
              )}
            </ActionButtons>
          )}
        </CardActions>
      </CardContent>
    </CardContainer>
  );
};

export default StoreCard;
